import { useState, useEffect } from 'react';
import { useLocation, useOutletContext } from 'react-router';
import { OutletContext } from '../../../layout/Layout';
import { useToast } from '@zeroedin-tech/zi-common-ui/lib';
import { OptionsBuilderItemTypes } from '../../../types/dataframes/options-builder-item-types';
import { Dimension } from '../../../api/analytics/Dimension';
import { Folder, TFolder } from '../../../api/foundational-elements/Folder';
import { FolderTypesEnum } from '../../../enums/folder-types-enum';
import { AlertVariant, Card, CardBody } from '@patternfly/react-core';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
	DataframeDataRetrievalRequest,
	DataframeDataRetrievalResponse,
	RunReportRequest,
	TDataframe,
	TDataframeOrder,
	TDateRange,
	TReport,
	TReportConditionalRules,
} from '../../../api/types';
import { Dataframe, TNewDataframe, TNewDataframeOrder } from '../../../api/dataframes/Dataframes';
import { Report } from '../../../api/reports/Reports';
import Loader from '../../../components/util/Loader';
import { DataBuilderPropsV2, DraggableMenuItemData } from '../../../types/databuilder/databuilder';
import { TNewDateRange } from '../../../api/types/TNewDateRange';
import ZiDataBuilderV2 from '../../../components/data-builder/ZiDataBuilderV2';
import { DataBuilderTypes } from '../../../enums/data-builder-types';
import './BuildReport.scss';
import {
	buildDataframeRequest,
	populateDroppedColumnsByDataframe,
	populateDroppedFactsByDataframe,
	populateDroppedFactsByKeyMeasure,
	populateDroppedFactsByKeyMeasureFact,
	populateDroppedFilterByDimension,
	populateDroppedFilterByDimensionAttribute,
	populateDroppedFiltersByDashboardFilters,
	populateDroppedFiltersByDataframe,
	populateDroppedRowsByDataframe,
	populateDroppedRowsByKeyMeasure,
	populateDroppedRowsByKeyMeasureFact,
} from '../../../hooks/DataBuilderHooks';
import { useApplication, useUser } from '../../../components/user/ApplicationProvider';
import { MultipartResponse } from '../../../helpers/multipart-response.helper';
import Preview, { IPreviewProps } from '../../../components/data-builder/Preview';
import { selectedPeriod } from '../../../helpers/selected-period.helper';
import { DateRange } from '../../../api/date-period-selector/DateRange';
import {
	addNewRecentReport,
	addNewRecentTable,
} from '../../../helpers/helper-components/recents-factory-helper';
import { TSharedEntity } from '../../../api/shared-entity/SharedEntity';
import ViewReport from './ViewReport';
import { Favorites, TFavorites } from '../../../api/favorites/Favorites';
import { Widget } from '../../../api/dashbboards/DashboardWidgets';
import { Dashboard } from '../../../api/dashbboards/Dashboards';
import { FilterOperatorEnum } from '../../../enums/operators.enum';
import { DimensionAttribute } from '../../../api/analytics/DimensionAttribute';
import { DatePeriodReformatter } from '../../../components/date-period-selector/DatePeriodFormatter';
import { ConditionalRule } from '../../../types/conditional-rules/conditional-rules';
import { SortTypes } from '../../../types/common/sort-types';
import _ from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import PageTitleSubheader from '../../../layout/subheader/PageTitleSubheader';
import { useCommonStoreContext } from '../../../components/common-store/CommonStoreProvider';
import DeleteConfirmationModal from '../../../helpers/helper-components/DeleteConfirmationModal';
import InsightsChartSidebar from '../../../components/data-builder/InsightsChartSidebar';
import { InsightsActionsRequest } from '../../../api/types/insights-actions-request';
import {
	buildInsightsActionsRequest,
	toInsightsPeriod,
	DEFAULT_INSIGHTS_MAX_ROWS,
} from '../../../helpers/insights-actions-request.helper';
import { getExtendedPrompt } from '../../../api/zone/getExtendedPrompt';

type Props = {
	type: DataBuilderTypes;
};

const BuildReport = (props: Props) => {
	const { reportId, tableId, dashboardId, presentationId } = useParams();
	const navigator = useNavigate();
	const { currentDatePeriods, measures, dimensions, unitTypes, periods, zoneSettings } =
		useApplication();
	const currentPeriod = selectedPeriod();
	const defaultPeriod =
		currentDatePeriods.find((dp) => dp.period === 3) ?? (DateRange.Default() as TDateRange);
	const [searchParams, _setSearhcParams] = useSearchParams();
	const queryParams = {
		reportId: searchParams.get('reportId'),
		tableId: searchParams.get('tableId'),
		dataframeId: searchParams.get('dataframeId'),
		measureId: searchParams.get('measureId'),
		measureFactId: searchParams.get('measureFactId'),
		dimensionAttributeId: searchParams.get('dimensionAttributeId'),
		dimensionId: searchParams.get('dimensionId'),
	};
	const { setSubSide } = useOutletContext<OutletContext>();
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const { addToast } = useToast();
	const [folders, setFolders] = useState<TFolder[]>([]);
	const [dataframe, setDataframe] = useState<TDataframe>();
	const [parentDataframe, setParentDataframe] = useState<TDataframe>();
	const [report, setReport] = useState<TReport>();
	const [name, setName] = useState<string>();
	const [folder, setFolder] = useState<TFolder>();
	const [selectedDate, setSelectedDate] = useState<TNewDateRange | undefined>({
		begin_date: currentPeriod.startPeriod
			? currentPeriod.startPeriod.begin_date
			: defaultPeriod.begin_date ?? 0,
		end_date: currentPeriod.endPeriod
			? currentPeriod.endPeriod.end_date
			: defaultPeriod.end_date ?? 0,
		period: currentPeriod.startPeriod
			? currentPeriod.startPeriod.period
			: defaultPeriod.period ?? 0,
		sequence: currentPeriod.startPeriod
			? currentPeriod.startPeriod.period
			: defaultPeriod.sequence ?? 0,
	});
	const [facts, setFacts] = useState<DraggableMenuItemData[]>([]);
	const [rows, setRows] = useState<DraggableMenuItemData[]>([]);
	const [columns, setColumns] = useState<DraggableMenuItemData[]>([]);
	const [filters, setFilters] = useState<DraggableMenuItemData[]>([]);
	const [parameters, setParameters] = useState<DraggableMenuItemData[]>([]);
	const [drillRows, setDrillRows] = useState<DraggableMenuItemData[]>([]);
	const [drillFilters, setDrillFilters] = useState<DraggableMenuItemData[]>([]);
	const [order, setOrder] = useState<(TDataframeOrder | TNewDataframeOrder)[]>([]);
	const [previewData, setPreviewData] =
		useState<MultipartResponse<DataframeDataRetrievalResponse>>();
	const [isPreviewLoading, setPreviewIsLoading] = useState<boolean>(false);
	const [showPreview, setShowPreview] = useState<boolean>(false);
	const [disablePreviewBtn, setDisablePreviewBtn] = useState<boolean>(true);
	const [isEdit, setIsEdit] = useState<boolean>(false);
	const [isReportView, setIsReportView] = useState<boolean>(false);
	const [isCreate, setIsCreate] = useState<boolean>(false);
	const [dataframeChanged, setDataframeChanged] = useState<boolean>(false);
	const [favorite, setFavorite] = useState<TFavorites>();
	const currentUser = useUser();
	const [widgetRow, setWidgetRow] = useState<number>(0);
	const pathname = useLocation().pathname;
	const isView = pathname.includes('/view/');
	const [datePeriodSelectorOverride, setDatePeriodSelectorOverride] = useState<TDateRange>();
	const [datePeriodSelectorLabel, setDatePeriodSelectorLabel] = useState<string>();
	const [datePeriodSelectorDisabledMessage, setDatePeriodSelectorDisabledMessage] =
		useState<string>();
	const [conditionalFormattingRules, setConditionalFormattingRules] = useState<
		TReportConditionalRules[]
	>([]);
	const [prevRetrievalRequest, setPrevRetrievalRequest] =
		useState<DataframeDataRetrievalRequest>();
	const [previewRequest, setPreviewRequest] = useState<DataframeDataRetrievalRequest>();
	const [hasReportLoaded, setHasReportLoaded] = useState<boolean>(false);
	const useLocationqueryParams = useLocation().search;
	const isCopy = useLocationqueryParams.includes('isCopy=true');
	const { sharedDashboardFilters, setCurrentSelectedTableReport, setCurrentSelectedDashboard } =
		useCommonStoreContext();
	const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false);
	const [isColTotalsChecked, setIsColTotalsChecked] = useState(false);
	const [isRowTotalsChecked, setIsRowTotalsChecked] = useState(false);

	// AI Insights
	const [insightsOpen, setInsightsOpen] = useState<boolean>(false);
	const [insightsActionsRequest, setInsightsActionsRequest] = useState<InsightsActionsRequest>();

	// validation
	const [validated, setValidated] = useState<
		'default' | 'warning' | 'success' | 'error' | undefined
	>();

	useEffect(() => {
		setIsLoading(true);
		setSubSide({ isLoading: true });

		if (dashboardId) {
			Widget.GetAll({ dashboard: dashboardId })
				.then((widgetsResponse) => {
					let row = widgetsResponse.length;
					if (row > 0) {
						row = widgetsResponse[row - 1].sizey + 1;
					}
					setWidgetRow(row);
				})
				.catch(() => {
					console.log('failed widget getall');
				});

			// The breadcrumb reads the originating dashboard from the shared store, which is
			// otherwise only populated by the dashboard pages themselves - so a reload here
			// would leave it unset.
			Dashboard.Get(+dashboardId)
				.then((dashboardResponse) => {
					setCurrentSelectedDashboard(dashboardResponse);
				})
				.catch(() => {
					console.log('failed dashboard get');
				});
		}

		if (reportId || tableId) {
			if (isView && (reportId || tableId)) {
				setIsReportView(true);
				setSubSide({ hideLeftSideBar: true });
			} else {
				setIsEdit(true);
			}

			setValidated('success');
		} else {
			if (!reportId) {
				setIsCreate(true);
			}

			if (isCopy) {
				setValidated('error');
			} else {
				setValidated('default');
			}
		}

		getPageData();
	}, []);

	useEffect(() => {
		// getPageData()'s plain setSubSide would drop the sidebar on a table -> table
		// navigation while insightsOpen stayed true, leaving the trigger out of sync.
		setInsightsOpen(false);
		getPageData();
		if (reportId) addNewRecentReport(reportId);
		if (tableId) addNewRecentTable(tableId);
	}, [window.location.pathname]);

	useEffect(() => {
		if (isView && (reportId || tableId)) {
			setIsReportView(true);
			setSubSide({
				hideLeftSideBar: true,
				subheaderContext: (
					<PageTitleSubheader
						pageTitle={''}
						isCollapsable={false}
					/>
				),
			});
			setIsEdit(false);
		} else {
			setSubSide({
				subheaderContext: (
					<PageTitleSubheader
						pageTitle={''}
						isCollapsable={false}
					/>
				),
			});
			setIsEdit(true);
		}
	}, [window.location.pathname]);

	// Populate dropped items from dataframe
	useEffect(() => {
		if (dataframe) {
			populateDroppedItems();
		}
	}, [dataframe]);

	useEffect(() => {
		if (dataframe) {
			setPreviewIsLoading(true);
			setShowPreview(true);
			checkDrillItems();
		}

		const handler = setTimeout(() => {
			if (!disablePreviewBtn) {
				onPreview();
			}
		}, 500);

		return () => {
			clearTimeout(handler);
		};
	}, [
		facts,
		rows,
		columns,
		filters,
		selectedDate,
		order,
		drillRows,
		drillFilters,
		datePeriodSelectorOverride,
		parameters,
	]);

	useEffect(() => {
		if (report && report.folder) {
			setFolder(folders.find((folder) => folder.id === report.folder));
		}
	}, [report]);

	// Mounted from BuildReport rather than from ZiDataBuilderV2 so it covers view
	// mode as well as edit mode. Passing insightsOpen down into builderProps too
	// would give two effects competing over the same rightSideBar slot.
	// Always the functional form: view mode's setSubSide({ hideLeftSideBar: true })
	// would be clobbered by a plain set.
	useEffect(() => {
		setSubSide((prev) => ({
			...prev,
			rightSideBar: insightsOpen ? (
				<InsightsChartSidebar
					name={name}
					previewData={previewData}
					insightsActionsRequest={insightsActionsRequest}
					insightsOpen={insightsOpen}
					setInsightsOpen={setInsightsOpen}
				/>
			) : undefined,
		}));
	}, [insightsOpen, insightsActionsRequest]);

	const checkDrillItems = () => {
		if (props.type === DataBuilderTypes.table) {
			const drillableRows = rows.filter((row) => row.data?.drillable);
			if (drillableRows.length) {
				if (!drillRows.length && !drillFilters.length) {
					setDrillRows(drillableRows.length ? [drillableRows[0]] : []);
				} else {
					if (
						drillRows.some((row) => !rows.includes(row)) ||
						drillRows.some((row) => !drillableRows.includes(row))
					) {
						setDrillRows([drillableRows[0]]);
						setDrillFilters([]);
					} else {
						if (drillableRows[0].id != drillRows[0].id) {
							setDrillRows([drillableRows[0]]);
							setDrillFilters([]);
						}
					}
				}
			} else {
				if (drillRows.length) {
					setDrillRows([]);
					setDrillFilters([]);
				}
			}
		}
	};

	const getPageData = () => {
		Folder.GetAll()
			.then((folders) => {
				if (folders) {
					const folderType =
						props.type === DataBuilderTypes.report
							? FolderTypesEnum.Report
							: FolderTypesEnum.Tables;
					const filteredFolders = folders.filter((f) => f.type === folderType);
					setFolders(filteredFolders);
				} else {
					setFolders([
						{
							id: -1,
							name: 'No folders found',
							type: 'dataframes',
							items: [],
						},
					]);
				}

				if (queryParams.reportId || reportId || queryParams.tableId || tableId) {
					const id =
						props.type === DataBuilderTypes.report
							? +(reportId ?? queryParams.reportId ?? 0)
							: +(tableId ?? queryParams.tableId ?? 0);
					getReportData(id);
					getFavoriteData();
				} else if (queryParams.dataframeId) {
					getDataframeData();
				} else if (
					queryParams.measureId &&
					queryParams.measureFactId &&
					(queryParams.dimensionAttributeId || queryParams.dimensionId)
				) {
					populateDroppedItems();
					createParentAndChildDataframes();
				}
			})
			.catch(() => {
				addToast('There was an issue retrieving data', AlertVariant.danger);
			});
	};

	const getReportData = (id: number) => {
		Report.Get(id, ['sharedReport', 'conditionalRules'])
			.then((response) => {
				setReport(response);
				setCurrentSelectedTableReport(response);

				if (!isCopy) {
					setName(response.name);
				}
				setConditionalFormattingRules(response.conditionalRules);
				setIsColTotalsChecked(response.show_col_totals ?? false);
				setIsRowTotalsChecked(response.show_row_totals ?? false);
				getDataframeData(+response.dataframe);
			})
			.catch(() => {
				addToast('There was an issue retrieving the report', AlertVariant.danger);
				setIsLoading(false);
				setSubSide({ isLoading: false });
			});
	};

	const getFavoriteData = () => {
		const id = DataBuilderTypes.report
			? +(reportId ?? queryParams.reportId ?? 0)
			: +(tableId ?? queryParams.tableId ?? 0);
		Favorites.Get(id, props.type)
			.then((response: TFavorites) => {
				if (response) {
					setFavorite(response);
				} else {
					setFavorite({
						owner: currentUser.id,
						entity_id: id,
						entity_type: props.type,
						name: report?.name ?? '',
					});
				}
			})
			.catch(() => {
				addToast(
					'There was an issue setting the report as a favorite',
					AlertVariant.danger
				);
			});
	};

	const createParentAndChildDataframes = () => {
		if (
			queryParams.measureId &&
			queryParams.measureFactId &&
			(queryParams.dimensionAttributeId || queryParams.dimensionId)
		) {
			const childFacts = populateDroppedFactsByKeyMeasure(
				+queryParams.measureId,
				+queryParams.measureFactId,
				measures,
				unitTypes
			);

			const parentFacts = populateDroppedFactsByKeyMeasureFact(
				+queryParams.measureFactId,
				measures,
				unitTypes
			);

			const childRows = populateDroppedRowsByKeyMeasure(
				dimensions,
				queryParams.dimensionId ? +queryParams.dimensionId : undefined,
				queryParams.dimensionAttributeId ? +queryParams.dimensionAttributeId : undefined
			);

			const parentRows = populateDroppedRowsByKeyMeasureFact(
				+queryParams.measureFactId,
				measures,
				dimensions
			);

			const childDataframeRequest: TNewDataframe | TDataframe = buildDataframeRequest(
				childFacts,
				childRows,
				[],
				[],
				'',
				[],
				false,
				false,
				undefined,
				undefined,
				true
			);

			const parentDataframeRequest: TNewDataframe | TDataframe = buildDataframeRequest(
				parentFacts,
				parentRows,
				[],
				[],
				'',
				[],
				false,
				false,
				undefined,
				undefined
			);

			Dataframe.NewDataframe(parentDataframeRequest as TNewDataframe)
				.then((parent: TDataframe) => {
					childDataframeRequest.parent = parent.id;
					Dataframe.NewDataframe(childDataframeRequest as TNewDataframe)
						.then((child: TDataframe) => {
							getDataframeData(child.id);
						})
						.catch((): void => {
							addToast('Error creating child dataframe.', AlertVariant.danger);
							setIsLoading(false);
							setSubSide({ isLoading: false });
						});
				})
				.catch((): void => {
					addToast('Error creating parent dataframe.', AlertVariant.danger);
					setIsLoading(false);
					setSubSide({ isLoading: false });
				});
		}
	};

	const getDataframeData = (dataframeId?: number) => {
		const id = dataframeId ?? queryParams.dataframeId;
		if (id) {
			Dataframe.Get(+id, ['datasets', 'filters', 'rowEntry', 'columnEntry', 'order'])
				.then((response) => {
					if (response.parent) {
						Dataframe.Get(response.parent, [
							'datasets',
							'filters',
							'rowEntry',
							'columnEntry',
							'order',
						])
							.then((parentResponse) => {
								setParentDataframe(parentResponse);
								setDataframe(response);
								if (response.order && response.order.length) {
									setOrder(response.order);
								} else {
									setOrder([
										{
											dataframe: response.id,
											direction: SortTypes.Asc,
											entity_id: response.rowEntry[0].entity_id,
											entity_type: response.rowEntry[0].entity_type,
											useSequence: false,
											pivotSort: '',
										},
									]);
								}
								setDisablePreviewBtn(false);
							})
							.catch(() => {
								addToast(
									'There was an issue retrieving the parent dataframe',
									AlertVariant.danger
								);
								setIsLoading(false);
								setSubSide({ isLoading: false });
							});
					} else {
						setDataframe(response);
						setOrder(response.order ? response.order : []);
						setDisablePreviewBtn(false);
					}
				})
				.catch(() => {
					addToast('There was an issue retrieving the dataframe', AlertVariant.danger);
					setIsLoading(false);
					setSubSide({ isLoading: false });
				});
		}
	};

	const saveDataframe = () => {
		const request = buildDataframeRequest(
			facts,
			rows,
			columns,
			filters,
			name ? name : '',
			order,
			(queryParams.reportId != void 0 || queryParams.dataframeId != void 0) &&
				!dataframe?.parent,
			queryParams.dataframeId ? false : true,
			dataframe,
			folder,
			true
		);

		setIsLoading(true);

		if (queryParams.dataframeId || isCopy) {
			Dataframe.NewDataframe(request as TNewDataframe)
				.then((_: TDataframe): void => {
					setDisablePreviewBtn(false);
					getDataframeData(_.id);
					saveReport(_.id);
				})
				.catch((): void => {
					addToast('Error creating dataframe.', AlertVariant.danger);
					setIsLoading(false);
				});
		} else {
			Dataframe.PatchDataframe(request as TNewDataframe)
				.then((_: TDataframe): void => {
					getDataframeData(_.id);
					saveReport(_.id);
				})
				.catch((): void => {
					addToast('Error updating dataframe.', AlertVariant.danger);
					setIsLoading(false);
				});
		}
	};

	const saveReport = (dataframeId: number) => {
		const conditionalRulesForSave = isCopy
			? conditionalFormattingRules.map(({ id: _id, report: _report, ...rule }) => rule)
			: conditionalFormattingRules;

		const request = {
			...(report && !isCopy && { id: report.id }),
			name: name ? name : '',
			description: '',
			folder: folder ? folder?.id : 0,
			dataframe: dataframeId,
			conditionalRules: conditionalRulesForSave,
			owner: currentUser.id,
			is_from_dataframe: report?.is_from_dataframe ?? queryParams.dataframeId ? true : false,
			is_table: props.type === DataBuilderTypes.table,
			show_col_totals: isColTotalsChecked,
			show_row_totals: isRowTotalsChecked,
		};

		if ((reportId || tableId) && !isCopy) {
			if (tableId) {
				request.id = +tableId;
			}
			if (reportId) {
				request.id = +reportId;
			}

			//keep previous owner id when updating
			if (report?.owner) {
				request.owner = +report?.owner;
			}

			Report.Patch(request)
				.then((_: TReport): void => {
					if (dashboardId) {
						navigator(`/analyze/dashboards/${dashboardId}`);
					} else if (presentationId) {
						navigator(`/present/edit/${presentationId}`);
					} else {
						getReportData(_.id);
					}
				})
				.catch((): void => {
					addToast(
						`Error updating ${
							props.type === DataBuilderTypes.table ? 'table' : 'report'
						}.`,
						AlertVariant.danger
					);
					setIsLoading(false);
				});
		} else {
			Report.New(request)
				.then((_: TReport): void => {
					if (dashboardId) {
						Widget.New({
							...Widget.Default(),
							name: _.name,
							dashboard: parseInt(dashboardId),
							report: _.id,
							widget_type: 'pivot_table',
							col: 0,
							row: widgetRow,
							sizex: 6,
							sizey: 6,
						})
							.then((_createWidgetResponse) => {
								setIsLoading(false);
								navigator(`/analyze/dashboards/${dashboardId}`);
							})
							.catch(() => {
								setIsLoading(false);
								addToast('Create dashboard widget failed.', AlertVariant.danger);
							});
					} else if (presentationId) {
						navigator(`/present/blank`);
					} else {
						setIsLoading(false);
						setIsEdit(true);
						navigator(
							`/${props.type === DataBuilderTypes.table ? 'table' : 'report'}/edit/${
								_.id
							}`
						);
					}
				})
				.catch((): void => {
					addToast(
						`Error creating ${
							props.type === DataBuilderTypes.table ? 'table' : 'report'
						}.`,
						AlertVariant.danger
					);
					setIsLoading(false);
				});
		}
	};

	const populateDroppedItems = () => {
		const dateRangeSelector: DraggableMenuItemData = {
			id: '-1',
			entityType: OptionsBuilderItemTypes.DatePeriodSelector,
			allowedZones: [],
			data: null,
			entity: {},
			itemType: 'date',
			static: true,
		};

		if (dataframe) {
			setFacts(populateDroppedFactsByDataframe(dataframe, measures));
			setRows(populateDroppedRowsByDataframe(dataframe, dimensions));
			checkDrillItems();
			setColumns(populateDroppedColumnsByDataframe(dataframe, dimensions));

			const sharedDashboardFilter = dashboardId
				? sharedDashboardFilters.find((filter) => filter.id === +dashboardId)
				: null;

			const sharedFilterItems =
				tableId && sharedDashboardFilter
					? sharedDashboardFilter.widgets.find((widget) => widget.itemId === +tableId)
							?.filters
					: [];

			if (sharedFilterItems?.length) {
				setDataframeChanged(true);
			}

			// Ensure to add the date range selector by default
			setFilters([
				...[dateRangeSelector],
				...(dashboardId
					? populateDroppedFiltersByDashboardFilters(
							sharedFilterItems ?? [],
							dataframe,
							dimensions,
							false
					  )
					: populateDroppedFiltersByDataframe(dataframe, dimensions)),
			]);

			setParameters([
				...(dashboardId
					? populateDroppedFiltersByDashboardFilters(
							sharedFilterItems ?? [],
							dataframe,
							dimensions,
							!isEdit
					  )
					: populateDroppedFiltersByDataframe(dataframe, dimensions, !isEdit)),
			]);
		} else if (
			queryParams.measureId &&
			queryParams.measureFactId &&
			(queryParams.dimensionAttributeId || queryParams.dimensionId)
		) {
			setFacts(
				populateDroppedFactsByKeyMeasure(
					+queryParams.measureId,
					+queryParams.measureFactId,
					measures,
					unitTypes
				)
			);
			setRows(
				populateDroppedRowsByKeyMeasure(
					dimensions,
					queryParams.dimensionId ? +queryParams.dimensionId : undefined,
					queryParams.dimensionAttributeId ? +queryParams.dimensionAttributeId : undefined
				)
			);
		}
	};

	const checkForSharedFiltersBeforeSave = (): void => {
		if (dashboardId && tableId) {
			const sharedDashboardFilter = sharedDashboardFilters.find(
				(filter) => filter.id === +dashboardId
			);

			if (sharedDashboardFilter) {
				const sharedFilterItems = sharedDashboardFilter.widgets.find(
					(widget) => widget.itemId === +tableId
				);

				if (
					sharedFilterItems &&
					sharedFilterItems.filters.length &&
					filters.length &&
					(sharedFilterItems.filters.some((obj1) =>
						filters.some(
							(obj2) =>
								obj1.entity_id === obj2.data?.id &&
								obj1.entity_type === obj2.entityType
						)
					) ||
						sharedFilterItems.filters.some((obj1) =>
							filters.some(
								(obj2) =>
									obj1.entity_id === obj2.data?.id &&
									obj1.entity_type === obj2.entityType &&
									obj1.value === obj2.data?.value &&
									obj1.operator === obj2.data?.operator
							)
						))
				) {
					setShowConfirmationModal(true);
				} else {
					onSave();
				}
			}
		} else {
			onSave();
		}
	};

	const onSave = (): void => {
		setShowConfirmationModal(false);

		if (dataframeChanged) {
			saveDataframe();
		} else {
			saveReport(dataframe?.id ?? 0);
		}
	};

	const onPreview = (): void => {
		setHasReportLoaded(false);
		if (dataframe) {
			setPreviewIsLoading(true);
			setShowPreview(true);

			// Hoisted so the insights request describes exactly the rows and filters
			// the retrieval query ran with, drill state included. Reading dataframe.*
			// instead would describe the saved definition, not what is on screen.
			const requestRows =
				props.type === DataBuilderTypes.table
					? [...rows.filter((row) => !row.data?.drillable), ...drillRows]
					: rows;
			const requestFilters = isView
				? [...parameters.filter((param) => param.data?.value), ...drillFilters]
				: props.type === DataBuilderTypes.table
				? [...filters.filter((filter) => filter.data?.value), ...drillFilters]
				: filters.filter((filter) => filter.data?.value);

			const newOverrideRequest = buildDataframeRequest(
				facts,
				requestRows,
				columns,
				requestFilters,
				'override',
				order,
				false,
				true,
				dataframe,
				folder
			);

			// Without any measure there is nothing to override with, and omitting the
			// override would silently fall back to the persisted definition — rendering
			// the items the user just removed as though they were still there.
			if (!newOverrideRequest.datasets.length) {
				setIsLoading(false);
				setSubSide({ isLoading: false });
				setPreviewIsLoading(false);
				return;
			}

			const request: DataframeDataRetrievalRequest = {
				dataframeId: dataframe ? dataframe.id : 0,
				begin_date: datePeriodSelectorOverride?.begin_date ?? selectedDate?.begin_date ?? 0,
				end_date: datePeriodSelectorOverride?.end_date ?? selectedDate?.end_date ?? 0,
				periodId: datePeriodSelectorOverride?.period ?? selectedDate?.period ?? 0,
				// Always send the override: it carries filters inherited from the dashboard,
				// so gating it on a diff against mount-time state silently drops them.
				override: newOverrideRequest,
			};
			setPrevRetrievalRequest(request);

			const requestsAreEqual = _.isEqual(request, prevRetrievalRequest);

			if (!requestsAreEqual) {
				Dataframe.Retrieve(request)
					.then((response: MultipartResponse<DataframeDataRetrievalResponse>): void => {
						setPreviewData(response);
						setPreviewRequest(request);
						setInsightsActionsRequest(
							buildInsightsActionsRequest({
								keyMeasures: facts.map((fact) => fact.data?.title ?? ''),
								// Both row AND column entries: on a pivot the column
								// dimensions are invisible to a rowEntry-only list.
								dimensions: [...requestRows, ...columns].map(
									(dim) => dim.data?.title ?? ''
								),
								columns: response.json?.columns ?? [],
								period: toInsightsPeriod({
									begin_date: request.begin_date,
									end_date: request.end_date,
									period: request.periodId,
								}),
								filtersApplied: requestFilters
									.filter(
										(filter) =>
											filter.entityType !=
											OptionsBuilderItemTypes.DatePeriodSelector
									)
									.map(
										(filter) =>
											`${filter.data?.title ?? ''}: ${
												filter.data?.value ?? ''
											}`
									),
								extendedPrompt: getExtendedPrompt(zoneSettings),
								csv: response.csvDataUnformatted,
								// Charts pin limitResults to 500; tables have no cap, so
								// the CSV is capped here instead.
								maxRows: DEFAULT_INSIGHTS_MAX_ROWS,
								logging: {
									user_id: currentUser.id,
									entity_type: props.type,
									entity_id: report?.id ?? 0,
								},
							})
						);
						setPreviewIsLoading(false);
						setTimeout(() => {
							setHasReportLoaded(true);
						}, 900);
					})
					.catch((): void => {
						addToast('Error fetching preview data.', AlertVariant.danger);
						setPreviewIsLoading(false);
					})
					.finally(() => {
						setIsLoading(false);
						// Merge rather than replace: a plain set here lands in a later
						// microtask than the insights effect above and would drop the
						// rightSideBar (and, in view mode, hideLeftSideBar) every time
						// the preview refreshes.
						setSubSide((prev) => ({ ...prev, isLoading: false }));
					});
			} else {
				setIsLoading(false);
				setSubSide((prev) => ({ ...prev, isLoading: false }));
				setPreviewIsLoading(false);
				setTimeout(() => {
					setHasReportLoaded(true);
				}, 900);
			}
		}
	};

	const runReport = () => {
		const request: RunReportRequest = {
			report_id: +(reportId ?? 0),
			file_type: 'csv',
			begin_date: selectedDate?.begin_date ?? 0,
			end_date: selectedDate?.end_date ?? 0,
			period_id: selectedDate?.period ?? 0,
		};
		Report.RunReport(request).catch(() => {
			addToast(
				'An error occured while attempting to Generate Run Report',
				AlertVariant.danger
			);
		});
	};

	const getReportSharedPermissions = () => {
		let userSharedRecord: { canEdit: boolean; canShare: boolean } = {
			canEdit: false,
			canShare: false,
		};
		if (!report || currentUser.id === report?.owner) {
			userSharedRecord = {
				canEdit: true,
				canShare: true,
			};
		} else {
			report?.sharedReport?.map((sc) => {
				const thisUser = (sc as TSharedEntity).shared_entity_users?.find(
					(u) => u.user === currentUser.id
				);
				const thisGroup = (sc as TSharedEntity).shared_entity_groups?.find(
					(u) => currentUser.groups.findIndex((ug) => ug === u.group) > -1
				);
				if (thisUser) {
					userSharedRecord = { canEdit: thisUser.can_edit, canShare: thisUser.can_share };
				} else if (thisGroup) {
					userSharedRecord = {
						canEdit: thisGroup.can_edit,
						canShare: thisGroup.can_share,
					};
				}
			});
		}

		return userSharedRecord;
	};

	const applyDrilldown = (name: string, isKMF: boolean, col?: string) => {
		if (!isKMF && drillRows.find((row) => row.data?.title.replace(/\s/g, '') === col)) {
			const exists = drillFilters.find(
				(filter) => filter.data?.title.replace(/\s/g, '') === col
			);

			if (exists) {
				const indexDrillRow = drillRows.findIndex(
					(item) => item.data?.title.replace(/\s/g, '') === col
				);

				const indexDrillFilter = drillFilters.findIndex(
					(item) => item.data?.title.replace(/\s/g, '') === col
				);

				setDrillRows(drillRows.slice(0, indexDrillRow + 1));
				setDrillFilters(drillFilters.slice(0, indexDrillFilter));
			} else {
				const drillItems = rows.filter((row) => row.data?.drillable);
				const currentIndex = drillItems.findIndex(
					(item) => item.data?.title.replace(/\s/g, '') === col
				);
				const drillItem = drillItems[currentIndex];

				if (drillItem.entityType === OptionsBuilderItemTypes.Dimension) {
					void Dimension.RetrievalWithType(drillItem.data?.id ?? 0).then((response) => {
						let newFilterItem: DraggableMenuItemData | null = null;
						const matchingFilterValue = response.items.find(
							(x) => x.value.toLowerCase() === name.toLowerCase()
						);

						newFilterItem = populateDroppedFilterByDimension(
							drillItem.data?.id ?? 0,
							dimensions
						);

						if (newFilterItem && newFilterItem.data) {
							newFilterItem.data.title = drillItem.data?.title ?? '';
							newFilterItem.data.drillable = false;
							newFilterItem.data.operator = FilterOperatorEnum.EQUALS;
							newFilterItem.data.value = matchingFilterValue?.id.toString();
							newFilterItem.data.isExistingValue = true;
							setDrillFilters([...drillFilters, newFilterItem]);

							if (drillItems[currentIndex + 1]) {
								setDrillRows([...drillRows, drillItems[currentIndex + 1]]);
							}
						}
					});
				} else if (drillItem.entityType === OptionsBuilderItemTypes.DimensionAttribute) {
					void DimensionAttribute.Retrieval(drillItem.data?.id ?? 0).then((response) => {
						let newFilterItem: DraggableMenuItemData | null = null;
						const matchingFilterValue = response.find(
							(x) => x.value.toLowerCase() === name.toLowerCase()
						);

						newFilterItem = populateDroppedFilterByDimensionAttribute(
							drillItem.data?.id ?? 0,
							dimensions
						);
						if (newFilterItem && newFilterItem.data) {
							newFilterItem.data.title = drillItem.data?.title ?? '';
							newFilterItem.data.drillable = false;
							newFilterItem.data.operator = FilterOperatorEnum.EQUALS;
							newFilterItem.data.value = matchingFilterValue?.id.toString();
							newFilterItem.data.isExistingValue = true;
							setDrillFilters([...drillFilters, newFilterItem]);

							if (drillItems[currentIndex + 1]) {
								setDrillRows([...drillRows, drillItems[currentIndex + 1]]);
							}
						}
					});
				} else if (drillItem.entityType === OptionsBuilderItemTypes.DateSeries) {
					if (selectedDate) {
						if (datePeriodSelectorOverride) {
							setDatePeriodSelectorDisabledMessage(undefined);
							setDatePeriodSelectorLabel(undefined);
							setDatePeriodSelectorOverride(undefined);
						} else {
							setDatePeriodSelectorDisabledMessage(
								'Please drill out if you would like to change the date filter.'
							);
							setDatePeriodSelectorLabel(name);
							setDatePeriodSelectorOverride(
								DatePeriodReformatter(name, selectedDate.period)
							);
						}
					}
				}
			}
		}
	};

	const updateConditionalFormattingRules = (rules: ConditionalRule[]) => {
		setConditionalFormattingRules(
			rules.map((rule) => {
				return {
					...rule,
					...{ report: report?.id ?? 0 },
				};
			})
		);
	};

	const builderProps: DataBuilderPropsV2 = {
		dataframe,
		parentDataframe,
		formLabel: props.type === DataBuilderTypes.report ? 'Report Name' : 'Table Name',
		onSave: checkForSharedFiltersBeforeSave,
		onPreview,
		isEdit,
		isView,
		measures,
		dimensions,
		unitTypes,
		periods,
		folders,
		selectedDate,
		setSelectedDate,
		facts,
		rows,
		columns,
		filters,
		setFacts,
		setRows,
		setColumns,
		setFilters,
		name: name ?? '',
		setName: setName,
		folder,
		setFolder,
		sidebarHeader: 'Add Data',
		sidebarSubheader: `Drag & drop data to add it to your ${
			props.type === DataBuilderTypes.report ? 'report' : 'table'
		}`,
		validated,
		setValidated,
		type: props.type,
		disablePreviewBtn,
		setHasChanges: setDataframeChanged,
		showLimitWarning: previewData?.json?.result_size?.limitReached,
		limitDimensionOptions: queryParams.dataframeId ? true : report?.is_from_dataframe,
		sharedPermissions: getReportSharedPermissions(),
		previewData,
		exportRequest: previewRequest,
		favorite: favorite,
		setFavorite: getFavoriteData,
		report,
		isWidgetSave: !!dashboardId,
		datePeriodSelectorLabel,
		datePeriodSelectorDisabledMessage,
		conditionalFormattingRules,
		updateConditionalFormattingRules,
		hideFolderSelection: isView,
		isColTotalsChecked: isColTotalsChecked,
		setIsColTotalsChecked: setIsColTotalsChecked,
		isRowTotalsChecked: isRowTotalsChecked,
		setIsRowTotalsChecked: setIsRowTotalsChecked,
		hideConfigureCalculationOption: true,
	};

	const previewProps: IPreviewProps = {
		data: previewData,
		facts,
		rows:
			props.type === DataBuilderTypes.table
				? [...rows.filter((row) => !row.data?.drillable), ...drillRows]
				: rows,
		columns,
		unitTypes,
		isLoading: isPreviewLoading,
		setIsLoading: setPreviewIsLoading,
		order: order ?? dataframe?.order?.[0] ?? undefined,
		setOrder,
		...(props.type === DataBuilderTypes.table && {
			ApplyDrilldownDrillIn: applyDrilldown,
			drillFilters,
			drillRows,
			hasDateDrill: !!datePeriodSelectorOverride,
			// Tables only. This same component renders /report/edit|view/:id, and an
			// unconditional handler put the sparkle on reports too - which then logged
			// insights against entity_type 'report'. Preview hides the trigger when
			// onToggleInsights is absent, so omitting it is the whole gate.
			onToggleInsights: () => setInsightsOpen((prev) => !prev),
		}),
		conditionalRules: conditionalFormattingRules,
		chartLoaded: hasReportLoaded,
		isColTotalsChecked: isColTotalsChecked,
		isRowTotalsChecked: isRowTotalsChecked,
		periodId: selectedDate?.period,
	};

	const getBuilderTemplate = () => {
		if (isEdit || isCreate) {
			return (
				<>
					{isLoading ? (
						<Loader />
					) : (
						<div>
							{isCopy && (
								<>
									<div className="pf-c-alert pf-m-inline pf-m-info">
										<p className="pf-c-alert__title display-inline">
											<FontAwesomeIcon
												size="2x"
												icon={faInfoCircle}
												className="bolder"
											/>
											<span className="nudge-right"></span>
											<div className="fs-large">
												{`Note! You are currently creating a copy of the ${
													props.type == DataBuilderTypes.table
														? 'table'
														: 'report'
												} '${report?.name ?? ''}'`}
											</div>
										</p>
									</div>
									<br />
								</>
							)}
							<ZiDataBuilderV2 {...builderProps} />
						</div>
					)}
				</>
			);
		} else if (isReportView) {
			return (
				<>
					{isLoading ? (
						<Loader />
					) : (
						<ViewReport
							folder={folder}
							folders={folders}
							name={name}
							onRunReport={runReport}
							parameters={parameters}
							setParameters={setParameters}
							previewData={previewData}
							setSelectedDate={setSelectedDate}
							isTable={props.type == DataBuilderTypes.table}
							reportId={report?.id ?? 0}
							userId={currentUser.id}
							sharedPermissions={getReportSharedPermissions()}
							isColTotalsChecked={isColTotalsChecked}
							setIsColTotalsChecked={setIsColTotalsChecked}
							isRowTotalsChecked={isRowTotalsChecked}
							setIsRowTotalsChecked={setIsRowTotalsChecked}
							dataframeId={dataframe?.id ?? 0}
							selectedDate={selectedDate}
							exportRequest={previewRequest}
						/>
					)}
				</>
			);
		}
	};

	return (
		<>
			<Card className="report-container">
				<CardBody>
					{getBuilderTemplate()}
					{showPreview && !isLoading && <Preview {...previewProps} />}
				</CardBody>
			</Card>
			<DeleteConfirmationModal
				isOpen={showConfirmationModal}
				onClose={() => {
					setShowConfirmationModal(false);
				}}
				onSubmit={onSave}
				titleText="Confirm Save"
				confirmLabel="Yes"
				cancelLabel="No"
				textLabel="Dashboard filters have been carried to this table and will be saved. Do you want to continue and save the changes?"
			/>
		</>
	);
};

export default BuildReport;
